import React, { useState, useContext, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { toast } from 'react-hot-toast';
import { AuthContext } from 'src/context/Auth';
import { getAPIHandler, postAPIHandler } from 'src/Apiconfig/service';
//import { useContext, useEffect,  useState } from "react";

export const FileUploadDialog = ({ open, onClose, wallet }) => {
  const [files, setFiles] = useState({});
  const { userData } = useContext(AuthContext);
  const [fileTypes, setFileTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        if (!wallet?.configurations || wallet.configurations.length === 0) {
          const response = await getAPIHandler({
            endPoint: 'wallet',
            id: 'configuration',
            source: 'wallets',
          });

          if (response.status === 200) {
            const matched = response.data.find(cfg =>
              cfg.name?.toLowerCase() === wallet.name?.toLowerCase()
            );

            const uploadTypes = matched?.configurations?.find(cfg => cfg.type === 'FILE_UPLOAD')?.fileTypes || [];
            setFileTypes(uploadTypes);
          }
        } else {
          const uploadTypes = wallet.configurations?.find(cfg => cfg.type === 'FILE_UPLOAD')?.fileTypes || [];
          setFileTypes(uploadTypes);
        }
      } catch (err) {
        console.error('Failed to fetch wallet config', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, [wallet]);


  if (!wallet || isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Loading Wallet Configuration...</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#888' }}>
            Please wait while we load supported file types...
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  const handleFileChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      setFiles((prev) => ({ ...prev, [`csvFile_${index}`]: file }));
    }
  };

  const handleUpload = async () => {
    const fileEntries = Object.entries(files);

    if (fileEntries.length === 0) {
      toast.error('Please upload at least one file.');
      return;
    }

    try {
      setIsLoading(true);
      localStorage.setItem(`uploadInProgress_${wallet.name}`, 'true');

      // 🧠 FIX: Use the actual index from the key ("csvFile_1" → 1)
      const fileDetails = fileEntries.map(([key, file]) => {
        const keyIndex = Number(key.split('_')[1]);
        const fileType = fileTypes?.[keyIndex]?.type || `CSV_${keyIndex}`;
        return {
          fileName: file.name,
          fileType,
        };
      });

      const transactionFilesUploadRequest = {
        source: wallet.name?.toUpperCase(),
        walletId: wallet.walletId,
        fileDetails,
      };

      const formDataToSend = new FormData();
      formDataToSend.append(
        'transactionFilesUploadRequest',
        new Blob([JSON.stringify(transactionFilesUploadRequest)], {
          type: 'application/json',
        })
      );

      // Append actual files
      fileEntries.forEach(([_, file]) => formDataToSend.append('files', file));

      const uploadResponse = await postAPIHandler({
        endPoint: 'fileUpload',
      //  id: wallet.userId || userData?.uid,
        data: formDataToSend,
      });

      localStorage.removeItem(`uploadInProgress_${wallet.name}`);

      const responses = uploadResponse?.data?.fileUploadResponses;
      if (Array.isArray(responses)) {
        const allSuccessful = responses.every((r) => r.successful);

        if (allSuccessful) {
          responses.forEach((item) => {
            toast.success(
              `Your file "${item.fileName}" of "${wallet.name}" was synced successfully!`,
              {
                duration: 8000,
                style: {
                  background: '#4caf50',
                  color: '#fff',
                  fontWeight: 600,
                  borderRadius: '8px',
                  padding: '14px 18px',
                  fontSize: '15px',
                },
                iconTheme: {
                  primary: '#fff',
                  secondary: '#22c55e',
                },
              }
            );
          });
          setFiles({});
          onClose();
        } else {
          responses.forEach((item) => {
            if (!item.successful) {
              toast.error(
                `File "${item.fileName}" of "${wallet.name}" failed to sync.`,
                {
                  duration: 8000,
                  style: {
                    background: '#ef4444',
                    color: '#fff',
                    fontWeight: 600,
                    borderRadius: '8px',
                    padding: '14px 18px',
                    fontSize: '15px',
                  },
                  iconTheme: {
                    primary: '#fff',
                    secondary: '#ef4444',
                  },
                }
              );
            }
          });
        }
      } else {
        toast.error('Unexpected response format from server', {
          style: {
            background: '#ef4444',
            color: '#fff',
            fontWeight: 600,
            borderRadius: '8px',
            padding: '14px 18px',
            fontSize: '15px',
          },
          iconTheme: { primary: '#fff', secondary: '#ef4444' },
        });
      }
    } catch (err) {
      console.error('Upload Error:', err);
      localStorage.removeItem(`uploadInProgress_${wallet.name}`);

      toast.error('Something went wrong during file upload', {
        style: {
          background: '#ef4444',
          color: '#fff',
          fontWeight: 600,
          borderRadius: '8px',
          padding: '14px 18px',
          fontSize: '15px',
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Files for {wallet.name}</DialogTitle>
      <DialogContent>
        {fileTypes.map((fileType, index) => {
          const extensions = fileType.supportedExtensions?.map((ext) =>
            ext.trim().toLowerCase() === '.xlxs' ? '.xlsx' : ext.trim()
          ) || ['.csv'];

          return (
            <Box key={index} mb={4}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#83818E' }}>
                {fileType.type}
              </Typography>
              <Box
                sx={{
                  border: '1px dashed #4A4950',
                  borderRadius: '8px',
                  padding: '30px',
                  textAlign: 'center',
                  backgroundColor: '#F5F5F5',
                }}
              >
                <img
                  src="https://img.icons8.com/fluency/48/csv.png"
                  alt="CSV Icon"
                  style={{ marginBottom: '10px' }}
                />
                <Box>
                  Drag and drop or{' '}
                  <label htmlFor={`file-upload-${index}`} style={{ color: '#A259FF', cursor: 'pointer' }}>
                    Browse
                  </label>
                </Box>
                <Box style={{ fontSize: '13px', color: '#888' }}>
                  We support files in {extensions.join(', ')} format
                </Box>
                <input
                  id={`file-upload-${index}`}
                  type="file"
                  accept={extensions.join(',')}
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileChange(e, index)}
                />
                {files[`csvFile_${index}`] && (
                  <Box mt={1} style={{ color: '#ccc', fontSize: '14px' }}>
                    Selected File: {files[`csvFile_${index}`].name}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          sx={{
            background: "linear-gradient(180deg, rgb(95,81,222) 0%, rgb(173,93,211) 100%)",
            color: 'white',
          }}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileUploadDialog;
